import React, { useMemo } from 'react';
import { FiCheck, FiLoader, FiRefreshCw, FiTrash2, FiZap } from 'react-icons/fi';

import { Button } from '../../ComponnentsUI/common/Button';
import { fileKind, formatFileSize } from './quickInferenceUtils';

interface Props {
  onRefresh: () => void;
  onReset: () => void;
  onRunInference: () => void;
  listLoading: boolean;
  submitting: boolean;
  parsingDicom: boolean;
  fileCount: number;
  files: File[];
  sessionCount: number;
}

export function QuickInferenceWizardHeader({
  onRefresh,
  onReset,
  onRunInference,
  listLoading,
  submitting,
  parsingDicom,
  fileCount,
  files,
  sessionCount,
}: Props) {
  const totalSize = useMemo(
    () => files.reduce((sum, file) => sum + file.size, 0),
    [files]
  );

  const typeCounts = useMemo(() => {
    const counts = { image: 0, video: 0, dicom: 0 };
    files.forEach((file) => {
      counts[fileKind(file)] += 1;
    });
    return counts;
  }, [files]);

  return (
    <div className="flex shrink-0 flex-col gap-4 border-b border-slate-100 px-4 py-4 dark:border-gray-700 sm:px-5 lg:flex-row lg:items-center lg:justify-between lg:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-md shadow-blue-600/25">
          <FiZap size={18} />
        </div>
        <div className="min-w-0">
          <h1 className="text-base font-bold tracking-tight text-slate-900 dark:text-white sm:text-lg">
            Playground
          </h1>
          <p className="text-xs text-slate-500 dark:text-gray-400">
            Upload media and run inference — no patient required
            {sessionCount > 0 ? ` · ${sessionCount} past session${sessionCount === 1 ? '' : 's'}` : ''}
          </p>
          {fileCount > 0 ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-blue-100">
                {fileCount} queued · {formatFileSize(totalSize)}
              </span>
              {typeCounts.image > 0 ? (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  {typeCounts.image} img
                </span>
              ) : null}
              {typeCounts.video > 0 ? (
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                  {typeCounts.video} vid
                </span>
              ) : null}
              {typeCounts.dicom > 0 ? (
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">
                  {typeCounts.dicom} dcm
                </span>
              ) : null}
              {parsingDicom ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                  <FiLoader size={10} className="animate-spin" />
                  Reading DICOM…
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={listLoading || submitting}
          className="col-span-1 justify-center text-slate-500 ring-1 ring-slate-200 dark:ring-gray-600"
        >
          <FiRefreshCw className={`mr-1.5 ${listLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          disabled={submitting || fileCount === 0}
          className="col-span-1 justify-center text-slate-500 ring-1 ring-slate-200 hover:text-red-600 dark:ring-gray-600"
        >
          <FiTrash2 className="mr-1.5" />
          Clear
        </Button>
        <Button
          size="sm"
          onClick={onRunInference}
          disabled={submitting || parsingDicom || fileCount === 0}
          className="col-span-2 bg-gradient-to-r from-slate-900 to-slate-800 px-5 font-semibold text-white shadow-lg shadow-slate-900/20 hover:from-slate-800 hover:to-slate-700 sm:col-span-1"
        >
          {submitting ? (
            <>
              <FiLoader className="mr-2 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <FiCheck className="mr-2" />
              Run inference{fileCount > 0 ? ` (${fileCount})` : ''}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
